import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import {
  Modal,
  Button,
  Input,
  Label,
  Toggle,
} from '@atrvd/saas-ui'
import { useCreateFacility, SAGE_CHECK_REFRESH_WARNING } from '@/hooks/useBanking'
import {
  facilityFormSchema,
  normalizeOptionalFacilityField,
  type FacilityFormData,
} from '@/lib/facility-form'
import { formatFacilityMutationError } from '@/lib/facility-errors'

/**
 * Default values used to (re-)initialise the Add form. Pulled out as a
 * module-level const so `reset(...)` after a successful create and the
 * `useForm` default-values prop share the same source of truth — when a
 * new field gets added to {@link FacilityFormData} the defaults flip in
 * exactly one place.
 */
const ADD_FACILITY_DEFAULTS: FacilityFormData = {
  name: '',
  sage_location_id: '',
  code: '',
  is_active: true,
}

/**
 * Props for {@link AddFacilityDialog}.
 *
 * @property open - Controls dialog visibility.
 * @property onOpenChange - Called when the dialog should open/close. The
 *                          parent owns the open flag, mirroring the
 *                          `EditFacilityDialog` contract for symmetry.
 */
interface AddFacilityDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Modal dialog for adding a new facility from the settings page.
 *
 * Deliberate mirror of {@link EditFacilityDialog}: same react-hook-form +
 * Zod stack, same shared {@link facilityFormSchema}, same field layout
 * (Name / Sage ID / Code / Status toggle). The DCP-154 PR
 * promises a *symmetric* add/edit experience; sharing the form contract
 * is the load-bearing piece of that promise — adding or tightening a
 * field constraint in `facilityFormSchema` now flows into both dialogs
 * automatically, and a future test suite can drive them with the same
 * Zod-validated payloads.
 *
 * The only intentional asymmetries vs `EditFacilityDialog`:
 *
 *   - Mutation hook is `useCreateFacility` (no `id` arg) rather than
 *     `useUpdateFacility({ id, data })`.
 *   - Form defaults are the {@link ADD_FACILITY_DEFAULTS} blank object
 *     instead of a `reset(...)` from a `facility` prop.
 *   - Title / submit-button copy say "Add" / "Create" instead of
 *     "Edit" / "Update".
 *
 * @param props - See {@link AddFacilityDialogProps}.
 */
export function AddFacilityDialog({ open, onOpenChange }: AddFacilityDialogProps) {
  const createFacility = useCreateFacility()

  /**
   * Last server-side mutation error, surfaced inline above the form so
   * it stays visible after the transient toast fades. Cleared on next
   * submit and on dialog close. The toast still fires for immediate
   * notification; the inline block is the durable record the operator
   * can re-read while figuring out how to fix the input.
   */
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FacilityFormData>({
    resolver: zodResolver(facilityFormSchema),
    defaultValues: ADD_FACILITY_DEFAULTS,
  })

  /**
   * Reset the form to blank defaults whenever the dialog opens. The
   * effect only re-runs on a `open` dependency change (RHF's `reset`
   * identity is stable per its docs), so this fires exactly once per
   * closed → open transition — not on every render-while-open. Also
   * clears any lingering server error from a previous open session.
   */
  useEffect(() => {
    if (open) {
      reset(ADD_FACILITY_DEFAULTS)
      setServerError(null)
    }
  }, [open, reset])

  /** Close the dialog and clear form state */
  const handleClose = () => {
    reset(ADD_FACILITY_DEFAULTS)
    setServerError(null)
    onOpenChange(false)
  }

  /**
   * Handle form submission — sends only the editable fields to the API.
   * On success shows a toast, closes the dialog, and clears the form. On
   * failure surfaces the server message (or a generic fallback) via an
   * error toast — `formatFacilityMutationError` recognises the
   * `sage_location_id` UNIQUE-violation shape and renders a copy-friendly
   * "Sage Facility ID already exists" message instead of the raw SQL
   * error.
   *
   * @param values - Validated form values produced by Zod.
   */
  const onSubmit = async (values: FacilityFormData) => {
    setServerError(null)
    try {
      const result = await createFacility.mutateAsync({
        name: values.name,
        sage_location_id: values.sage_location_id,
        // Code is optional in the DB — persist empty input as null so the
        // table cell renders the fallback dash instead of an empty string.
        code: normalizeOptionalFacilityField(values.code),
        is_active: values.is_active,
      })
      toast.success('Facility created successfully')
      // The facility itself saved; only the follow-up re-link of its Sage checks
      // failed (DCP-308 review #5). Warn rather than fail so the operator knows
      // Outstanding Checks may lag instead of silently reading a stale cache.
      if (result.checkRefreshError) {
        toast.warning(SAGE_CHECK_REFRESH_WARNING)
      }
      handleClose()
    } catch (error) {
      const errorMessage = formatFacilityMutationError(error, 'Failed to create facility')
      setServerError(errorMessage)
      toast.error(errorMessage)
    }
  }

  const currentIsActive = watch('is_active')

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title="Add Facility"
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={createFacility.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-facility-form"
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={createFacility.isPending}
          >
            {createFacility.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Create
          </Button>
        </div>
      }
    >
      <form
        id="add-facility-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 px-1"
      >
        {/*
          Inline server-error block — sibling to the per-field Zod errors
          below. Stays visible until the next submit attempt or dialog
          close, unlike the transient toast.
        */}
        {serverError && (
          <div
            role="alert"
            className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
          >
            {serverError}
          </div>
        )}

        {/* Facility Name */}
        <div className="space-y-2">
          <Label htmlFor="add-facility-name">
            Facility Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="add-facility-name"
            placeholder="Enter facility name"
            disabled={createFacility.isPending}
            {...register('name')}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        {/* Sage Facility ID */}
        <div className="space-y-2">
          <Label htmlFor="add-facility-sage-id">
            Sage Facility ID <span className="text-destructive">*</span>
          </Label>
          <Input
            id="add-facility-sage-id"
            placeholder="Enter Sage location ID"
            disabled={createFacility.isPending}
            {...register('sage_location_id')}
          />
          {errors.sage_location_id && (
            <p className="text-sm text-destructive">{errors.sage_location_id.message}</p>
          )}
        </div>

        {/* Facility Code */}
        <div className="space-y-2">
          <Label htmlFor="add-facility-code">Facility Code</Label>
          <Input
            id="add-facility-code"
            placeholder="Enter facility code (optional)"
            disabled={createFacility.isPending}
            {...register('code')}
          />
        </div>

        {/* Status Toggle */}
        <div className="space-y-2">
          <Label>Status</Label>
          <Toggle
            checked={currentIsActive ?? false}
            onChange={(checked: boolean) => setValue('is_active', checked)}
            disabled={createFacility.isPending}
            label={currentIsActive ? 'Active' : 'Inactive'}
            labelPosition="right"
          />
        </div>
      </form>
    </Modal>
  )
}
